import { State } from './state-class';

export interface StateConfig {
  numImages: number;
}

export class SliceObject {
  excludedImages: Set<string>;
  selectedImages: Set<string>;
  private states: Record<string, State>;

  constructor(states: Record<string, State>) {
    this.excludedImages = new Set<string>();
    this.selectedImages = new Set<string>();
    this.states = states;
  }

  getImageState(imageIndex: number): string | null {
    if (!Number.isInteger(imageIndex)) {
      throw new TypeError(`Image Index "${imageIndex}" must be an integer.`);
    }

    if (imageIndex < 0) {
      throw new Error(`Image Index "${imageIndex}" cannot be negative.`);
    }

    const allStates = this.getAllStateKeys();

    for (let i = 0; i < allStates.length; i++) {
      const stateName = allStates[i];
      const stateObj = this.getState(stateName);
      const startIndex = stateObj.getStart();
      const endIndex = stateObj.getEnd();
      const numImages = stateObj.getNumImages();

      if (
        imageIndex < numImages
        && imageIndex >= startIndex
        && imageIndex <= endIndex
      ) {
        return stateName;
      }
    }
    return null;
  }

  getBreathhold() {
    if (!this.states.BREATHHOLD) {
      throw new Error('No breathhold state found');
    }
    return this.states.BREATHHOLD;
  }

  addReplaceNewState(stateName: string, numImages: number) {
    this.states[stateName] = new State(stateName, numImages);
  }

  addExcludedImage(imageUID: string): void {
    if (this.isImageExcluded(imageUID)) {
      this.removeExcludedImage(imageUID);
      return;
    }
    this.excludedImages.add(imageUID);
  }

  addSelectedImage(imageUID: string): void {
    if (this.isImageSelected(imageUID)) {
      this.removeSelectedImage(imageUID);
      return;
    }
    this.selectedImages.add(imageUID);
  }

  isImageSelected(imageUID: string): boolean {
    return this.selectedImages.has(imageUID);
  }

  removeSelectedImage(imageUID: string): void {
    if (!this.selectedImages.has(imageUID)) {
      throw new Error(
        `Image UID "${imageUID}" is not present in the selected images set.`,
      );
    }
    this.selectedImages.delete(imageUID);
  }

  getSelectedImages(): Set<string> {
    return this.selectedImages;
  }

  isImageExcluded(imageUID: string): boolean {
    return this.excludedImages.has(imageUID);
  }

  removeExcludedImage(imageUID: string): void {
    if (!this.excludedImages.has(imageUID)) {
      throw new Error(
        `Image UID "${imageUID}" is not present in the excluded images set.`,
      );
    }
    this.excludedImages.delete(imageUID);
  }

  getExcludedImages(): Set<string> {
    return this.excludedImages;
  }

  getState(stateName: string): State {
    return this.states[stateName];
  }

  getStartEnd(stateName: string): [number, number] {
    const state = this.states[stateName];
    return [state.getStart(), state.getEnd()];
  }

  getIsImageEnd(imageIndex: number): boolean {
    if (!Number.isInteger(imageIndex)) {
      throw new TypeError(`Image Index "${imageIndex}" must be an integer.`);
    }

    if (imageIndex < 0) {
      throw new Error(`Image Index "${imageIndex}" cannot be negative.`);
    }

    const stateName = this.getImageState(imageIndex);
    if (!stateName) return false;

    const state = this.getState(stateName);
    return state.getEnd() === imageIndex;
  }

  getIsImageStart(imageIndex: number): boolean {
    if (!Number.isInteger(imageIndex)) {
      throw new TypeError(`Image Index "${imageIndex}" must be an integer.`);
    }

    if (imageIndex < 0) {
      throw new Error(`Image Index "${imageIndex}" cannot be negative.`);
    }

    const stateName = this.getImageState(imageIndex);
    if (!stateName) return false;

    const state = this.getState(stateName);
    return state.getStart() === imageIndex;
  }

  getStates(): Record<string, State> {
    return this.states;
  }

  getAllStateKeys(): string[] {
    return Object.keys(this.states);
  }

  getStateColor(stateName: string): string {
    return this.states[stateName].getColor();
  }

  updateStartEnd(
    stateName: string,
    startPhase: number,
    endPhase: number,
  ): void {
    this.states[stateName].updateStartEnd(startPhase, endPhase);
  }

  //  ToDo: Make it so the end start handlers of intersecting states adjust instead of return.
  updateStartEndById(
    id: string, // end or start
    stateName: string,
    phase: number,
  ): void {
    const target = this.states[stateName];

    const stateOfPhase = this.getImageState(phase);

    if (stateOfPhase && stateOfPhase !== stateName) return;

    if (id === 'end') {
      target.updateEnd(phase);
    }
    else {
      target.updateStart(phase);
    }
  }

  updateStart(stateName: string, newPhase: number): void {
    this.states[stateName].updateStart(newPhase);
  }

  updateEnd(stateName: string, newPhase: number): void {
    this.states[stateName].updateEnd(newPhase);
  }
}

export function createSliceObject(
  stateConfigs: Record<string, StateConfig>,
): SliceObject {
  const states: Record<string, State> = {};

  Object.keys(stateConfigs).forEach((stateName) => {
    const config = stateConfigs[stateName];
    states[stateName] = new State(stateName, config.numImages);
  });

  return new SliceObject(states);
}
